import React, { FC, useContext } from 'react';

import {
  getI18nData,
  sendBeaconEvent,
  BeaconEventMarketingPageElementLocation,
  BeaconEventMarketingPageElementType,
  useSiteMetadata,
  LinkInterface,
  ExtendedDrupalNode
} from '@lucid/utils';
import { Container, Link, LayoutContext, LanguageSwitcher } from '@lucid/components-react';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import { FooterSocialIconsNewJsonEdge } from 'generated/drupal-graphql';
import { mqMax, LucidTheme } from '@lucid/theme';

export interface FooterProps {
  entityTranslations?: ExtendedDrupalNode[];
  menu: LinkInterface[];
}

const FooterWrapper: FC<FooterProps> = ({ entityTranslations, menu }) => {
  const { language } = useContext(LayoutContext);
  const { shortLangPrefix, i18nKey } = language;

  const { allFooterSocialIconsNewJson, i18nData } = useStaticQuery(
    graphql`
      {
        allFooterSocialIconsNewJson {
          edges {
            node {
              name
              logo
              url
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_footer_about_privacy
                mktg_footer_legal
              }
            }
          }
        }
      }
    `
  );

  const i18n = getI18nData(i18nData, i18nKey);
  const { productUrl } = useSiteMetadata();
  const socialIcons = allFooterSocialIconsNewJson.edges as FooterSocialIconsNewJsonEdge[];
  const defaultLinks = [
    {
      url: withPrefix('/privacy'),
      text: i18n('mktg-footer-about-privacy')
    },
    {
      url: withPrefix('/legal'),
      text: i18n('mktg-footer-legal')
    }
  ];

  return (
    <footer
      css={(theme: LucidTheme) => ({
        paddingTop: theme.space[6],
        background: theme.colors.coolGrey[0]
      })}
    >
      <Container displayFlex className="footer-top">
        <div
          className="footer-top__links"
          css={(theme: LucidTheme) => ({
            display: 'grid',
            flexWrap: 'wrap',
            flex: '0 1 100%',
            gridTemplateColumns: `repeat(${menu.length <= 5 ? menu.length : 5}, 1fr)`,
            padding: `${theme.space[4]}px 0`,
            [mqMax.tabletSmall]: {
              gridTemplateColumns: 'repeat(3, 1fr)'
            },
            [mqMax.mobile]: {
              gridTemplateColumns: 'repeat(2, 1fr)'
            }
          })}
        >
          {menu.map(item => {
            return (
              <ul
                key={item.title}
                className="footer-top__link-group"
                css={{
                  flex: '0 1 20%',
                  listStyle: 'none',
                  margin: '0 0 2rem 0',
                  [mqMax.tablet]: {
                    flex: '0 1 31%',
                    marginBottom: '2.5rem'
                  },
                  [mqMax.mobile]: {
                    flex: '0 1 49%'
                  }
                }}
              >
                <li
                  className="footer-top__link-group-heading"
                  css={(theme: LucidTheme) => ({
                    fontSize: theme.fontSizes[3],
                    fontWeight: theme.fontWeights.bolder,
                    marginBottom: '0.75rem'
                  })}
                >
                  {item.title}
                </li>
                {item.links?.map(subitem => {
                  let href = subitem.link!.url!.path;
                  if (productUrl && subitem.fieldProductLink) {
                    href = `${productUrl}${shortLangPrefix !== 'en' ? `/${shortLangPrefix}` : ''}${href}`;
                  }
                  if (subitem.fieldLinkOverride) {
                    href = subitem.fieldLinkOverride;
                  }
                  const beaconEvent = subitem.fieldBeaconElementDetail
                    ? {
                      name: 'clickedMarketingPageElement',
                      elementLocation: BeaconEventMarketingPageElementLocation.footer,
                      elementDetail: subitem.fieldBeaconElementDetail,
                      elementType: BeaconEventMarketingPageElementType.link
                    }
                    : null;

                  return (
                    <li
                      key={subitem.title}
                      className="footer-top__link-group-item"
                      css={{
                        margin: '0 0 0.75rem',
                        paddingRight: '1.5rem',
                      }}
                    >
                      <Link
                        beaconEvent={beaconEvent}
                        className="footer-top__link-group-link"
                        css={(theme: LucidTheme) => ({
                          fontSize: theme.fontSizes[3],
                          fontWeight: theme.fontWeights.regular
                        })}
                        onClick={() => subitem.fieldBeaconElementDetail}
                        to={href}
                        underline="hover"
                        variant="subNavLink"
                      >
                        {subitem.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
      </Container>

      <div
        className="footer-bottom"
        css={(theme: LucidTheme) => ({
          padding: '1.5rem 0',
          background: theme.colors.coolGrey[5]
        })}
      >
        <Container displayFlex css={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <div
            className="footer-bottom__left"
            css={{
              flex: '0 1 40%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'baseline',
              [mqMax.tabletSmall]: {
                flex: '1 1 100%'
              }
            }}
          >
            <LanguageSwitcher
              disabledLanguages={['da', 'sv']}
              entityTranslations={entityTranslations!}
              shortLangPrefix={shortLangPrefix}
            />
            {defaultLinks.map(link => (
              <Link
                key={link.text}
                css={{
                  marginRight: '1rem',
                  fontWeight: 400,
                  '&:last-child': { marginRight: '0' }
                }}
                to={link.url}
                variant="subNavLink"
              >
                {link.text}
              </Link>
            ))}
          </div>
          <div
            className="footer-bottom__right"
            css={{
              flex: '0 1 60%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              [mqMax.tabletSmall]: {
                flex: '1 1 100%',
                marginTop: '2rem',
                flexWrap: 'wrap'
              }
            }}
          >
            <ul
              className="footer-bottom__social-links"
              css={(theme: LucidTheme) => ({
                display: 'flex',
                flex: '0 1 215px',
                margin: 0,
                listStyle: 'none',
                color: theme.colors.primary,
                justifyContent: 'space-between',
                [mqMax.tabletSmall]: {
                  flex: '1 1 290px'
                }
              })}
            >
              {socialIcons.map(socialIcon => {
                const icon = socialIcon.node;
                return (
                  <li key={icon.name!}>
                    <a
                      href={icon.url!}
                      onClick={() =>
                        sendBeaconEvent({
                          name: 'clickedMarketingPageElement',
                          elementLocation: BeaconEventMarketingPageElementLocation.footer,
                          elementDetail: icon.name,
                          elementType: BeaconEventMarketingPageElementType.link
                        })}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <img
                        alt={icon.name!}
                        css={{ height: '20px' }}
                        loading="lazy"
                        src={icon.logo!}
                      />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
          <div
            css={{
              marginTop: '.25rem',
              fontSize: '0.75rem',
              flex: '1 1 100%',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <span aria-label="Copyright" role="img">
              &copy;
            </span>
            {` ${new Date().getFullYear()} Lucid Software Inc.`}
          </div>
        </Container>
      </div>
    </footer>
  );
};

export default FooterWrapper;
